import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';

import type {
  DrawingPrimitive,
  DrawingScene,
  Length,
  MembraneTakeoff,
  ValidationMessage,
  WindZoneGeometry,
  ZoneResult,
} from '../../gen/ejot/roofing/v1/types';
import { formatArea, formatCount, formatLength, formatLengthMetres, formatQuantityWithUnit } from '../../shared/format';
import { usePinchGesture, viewBoxAfterPinch } from '../../shared/usePinchGesture';
import { ActionableDiagnostic } from '../diagnostics/ActionableDiagnostic';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import { DEFAULT_DRAWING_DISPLAY_SETTINGS, type DrawingDisplaySettings } from '../view/drawingDisplaySettings';
import { DxfExportButton } from './DxfExportButton';
import type { LayerVisibility } from '../roof-plan-editor/render';
import { windPlanBounds } from './WindZonePlan';

const MEMBRANE_COURSE_TAG_LAYER = 'membrane-course-tags';
const FIXING_SPACING_TAG_LAYER = 'fixing-spacing-tags';
const FIXING_POINTS_LAYER = 'fixing-points';
const MEMBRANE_SHEETS_LAYER = 'membrane-sheets';
const MEMBRANE_STOCK_METADATA_LAYER = 'membrane-stock-metadata';
const MEMBRANE_LABEL_HEIGHT_PREFIX = 'membrane-label-mm:';
const MIN_VIEW_SCALE = 0.05;
const MAX_VIEW_SCALE = 20;
/** Backend fixing circles already carry the requested drawing radius in world millimetres. */
const FIXING_MARKER_SCALE = 1;

/**
 * Point-level fixing view rendered from the backend DrawingScene.
 *
 * The browser interprets renderer-agnostic drawing primitives only. It never
 * turns density, pressure or overlap spacing into installation coordinates;
 * those coordinates and row guides must already exist in the backend scene.
 */
export function CalculatedFixingPlan({
  scene,
  zones,
  results,
  messages,
  layers,
  displaySettings = DEFAULT_DRAWING_DISPLAY_SETTINGS,
  selectedZoneId,
  onZoneSelect,
  exportFileName,
  variant = 'fixing',
  membraneTakeoff,
  onLayerToggle,
}: {
  scene: DrawingScene | undefined;
  zones: readonly WindZoneGeometry[];
  results: readonly ZoneResult[];
  messages: readonly ValidationMessage[];
  layers: LayerVisibility;
  displaySettings?: DrawingDisplaySettings;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string | undefined) => void;
  exportFileName?: string;
  /** 'membrane' swaps the bottom table for a membrane material takeoff and exposes membrane controls. */
  variant?: 'fixing' | 'membrane';
  membraneTakeoff?: MembraneTakeoff;
  /**
   * Toggles a drawing layer. Supplied, the drawing carries its own layer
   * controls; without it the drawing is read-only and they are not shown.
   */
  onLayerToggle?: (layerId: string, visible: boolean) => void;
}) {
  if (zones.length === 0 || results.length === 0) {
    return (
      <div className="viewer-overlay" role="status" aria-live="polite">
        <ActionableDiagnostic
          messages={messages}
          fallback="Complete the roof geometry, wind inputs and product selection, and the fixing layout appears here automatically."
        />
      </div>
    );
  }

  const hasScene = Boolean(scene && (scene.primitives?.length ?? 0) > 0);
  const fallback = windPlanBounds(zones, []);
  const bounds = sceneBounds(scene, fallback);
  const spanX = Math.max(bounds.maxX - bounds.minX, 1);
  const spanY = Math.max(bounds.maxY - bounds.minY, 1);
  const drawingSpan = Math.max(spanX, spanY);
  const padding = drawingSpan * 0.04;

  return (
    <div className="wind-zone-plan" aria-label="Calculated fixing layout">
      <div className="wind-zone-plan-canvas">
        {hasScene && scene ? (
          <InteractiveFixingScene
            scene={scene}
            layers={layers}
            bounds={bounds}
            drawingSpan={drawingSpan}
            padding={padding}
            variant={variant}
            displaySettings={displaySettings}
            onLayerToggle={onLayerToggle}
          />
        ) : (
          <div className="viewer-overlay" role="status" aria-live="polite">
            <ActionableDiagnostic
              messages={messages}
              fallback="Zone-level fixing values are available, but this resistance basis returned no point-level drawing. Installation coordinates are only ever drawn from the calculated result, never derived here from engineering spacing."
            />
          </div>
        )}
        <CoordinateIndicator mode="2d" visible={layers.axes !== false} />
      </div>

      <div className="wind-zone-plan-footer">
        <DxfExportButton scene={scene} fileName={exportFileName ?? scene?.id ?? 'drawing'} />

        {variant === 'membrane' ? (
          <MembraneTakeoffTable takeoff={membraneTakeoff} />
        ) : (
          <div className="engineering-table-scroll">
            <table className="engineering-table" aria-label="Backend fixing layout values by zone">
              <thead>
                <tr>
                  <th scope="col">Zone</th>
                  <th scope="col" className="numeric">Density</th>
                  <th scope="col" className="numeric">Overlap pitch</th>
                  <th scope="col" className="numeric">Fixings</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <tr key={result.zone_id} className={result.zone_id === selectedZoneId ? 'is-selected' : undefined}>
                    <td>
                      {onZoneSelect && result.zone_id ? (
                        <button
                          type="button"
                          className="text-button"
                          aria-pressed={result.zone_id === selectedZoneId}
                          onClick={() => onZoneSelect(result.zone_id === selectedZoneId ? undefined : result.zone_id)}
                        >
                          {result.display_label || result.zone_id}
                        </button>
                      ) : (result.display_label || result.zone_id || '—')}
                    </td>
                    <td className="numeric">
                      {formatQuantityWithUnit(result.layout?.installable_density, 'Unavailable')}
                    </td>
                    <td className="numeric">
                      {formatLength(result.layout?.spacing_in_overlap, 'Unavailable')}
                    </td>
                    <td className="numeric">
                      {result.layout?.total_fixings === undefined
                        ? 'Unavailable'
                        : `${formatCount(result.layout.total_fixings)} pcs`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="field-note">
          {variant === 'membrane'
            ? 'Membrane pieces, overlaps and fixing points come from the backend DrawingScene. The takeoff table above sums the same generated membrane pieces, including the material consumed by overlaps.'
            : 'Fixing-point coordinates, row guides and zone geometry come from the backend DrawingScene. The same scene contract is consumed by the PDF renderer, and the backend scene builder emits exactly the zone fixing total returned by the calculation. The interface only renders those supplied primitives.'}
        </p>
      </div>
    </div>
  );
}

function MembraneTakeoffTable({ takeoff }: { takeoff: MembraneTakeoff | undefined }) {
  const zones = takeoff?.zones ?? [];

  if (zones.length === 0) {
    return (
      <p className="field-note">
        Membrane takeoff will appear once the backend generates membrane pieces for this laying plan.
      </p>
    );
  }

  return (
    <div className="engineering-table-scroll">
      <table className="engineering-table membrane-takeoff-table" aria-label="Membrane material takeoff by zone">
        <thead>
          <tr>
            <th scope="col">Zone</th>
            <th scope="col" className="numeric">Membrane Width</th>
            <th scope="col" className="numeric">Total Length</th>
            <th scope="col" className="numeric">Total Area</th>
          </tr>
        </thead>
        <tbody>
          {zones.map((zone) => (
            <tr key={zone.zone_id || zone.display_label}>
              <td>{zone.display_label || zone.zone_id || '—'}</td>
              <td className="numeric">{formatMembraneWidths(zone.membrane_widths)}</td>
              <td className="numeric">{formatLengthMetres(zone.total_length, 'Unavailable')}</td>
              <td className="numeric">{formatArea(zone.total_area, 'Unavailable', 2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="is-total">
            <th scope="row">Total</th>
            <td className="numeric" />
            <td className="numeric">{formatLengthMetres(takeoff?.total_length, 'Unavailable')}</td>
            <td className="numeric">{formatArea(takeoff?.total_area, 'Unavailable', 2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function formatMembraneWidths(widths: Length[] | undefined): string {
  if (!widths || widths.length === 0) return 'Unavailable';
  return widths.map((width) => formatLengthMetres(width, 'Unavailable')).join(' / ');
}

interface ViewBoxState {
  x: number;
  y: number;
  width: number;
  height: number;
}

function InteractiveFixingScene({
  scene,
  layers,
  bounds,
  drawingSpan,
  padding,
  variant,
  displaySettings,
  onLayerToggle,
}: {
  scene: DrawingScene;
  layers: LayerVisibility;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  drawingSpan: number;
  padding: number;
  variant: 'fixing' | 'membrane';
  displaySettings: DrawingDisplaySettings;
  onLayerToggle?: (layerId: string, visible: boolean) => void;
}) {
  const showMembraneLabels = layers[MEMBRANE_COURSE_TAG_LAYER] !== false;
  const showFixingTags = layers[FIXING_SPACING_TAG_LAYER] !== false;
  const svgRef = useRef<SVGSVGElement>(null);
  const membraneStripHeight = useMemo(
    () => medianMembraneStripHeight(scene.primitives ?? []),
    [scene],
  );
  const dragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    viewBox: ViewBoxState;
  }>();
  const { minX, minY, maxX, maxY } = bounds;
  const baseViewBox = baseSceneViewBox({ minX, minY, maxX, maxY }, padding);
  const [viewBox, setViewBox] = useState<ViewBoxState>(baseViewBox);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    setViewBox(baseSceneViewBox({ minX, minY, maxX, maxY }, padding));
    dragRef.current = undefined;
    setDragging(false);
  }, [scene.id, minX, minY, maxX, maxY, padding]);

  const zoom = (factor: number, clientX?: number, clientY?: number) => {
    setViewBox((current) => {
      const requestedWidth = current.width * factor;
      const width = Math.min(baseViewBox.width * MAX_VIEW_SCALE, Math.max(baseViewBox.width * MIN_VIEW_SCALE, requestedWidth));
      const effectiveFactor = width / current.width;
      const height = current.height * effectiveFactor;
      const svg = svgRef.current;
      if (svg && clientX !== undefined && clientY !== undefined) {
        const rect = svg.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const relativeX = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
          const relativeY = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
          const anchorX = current.x + relativeX * current.width;
          const anchorY = current.y + relativeY * current.height;
          return {
            x: anchorX - relativeX * width,
            y: anchorY - relativeY * height,
            width,
            height,
          };
        }
      }
      return {
        x: current.x + (current.width - width) / 2,
        y: current.y + (current.height - height) / 2,
        width,
        height,
      };
    });
  };

  const reset = () => setViewBox(baseSceneViewBox({ minX, minY, maxX, maxY }, padding));

  const pinch = usePinchGesture({
    onGestureStart: () => {
      dragRef.current = undefined;
      setDragging(false);
    },
    onPinch: (update) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      setViewBox((current) => viewBoxAfterPinch(current, update, rect));
    },
  });

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    if (pinch.down(event)) return;
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      viewBox,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (pinch.move(event)) return;
    const drag = dragRef.current;
    const svg = svgRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const deltaX = (event.clientX - drag.clientX) * (drag.viewBox.width / rect.width);
    const deltaY = (event.clientY - drag.clientY) * (drag.viewBox.height / rect.height);
    setViewBox({
      ...drag.viewBox,
      x: drag.viewBox.x - deltaX,
      y: drag.viewBox.y - deltaY,
    });
  };

  const endPointerDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    const wasGesture = pinch.up(event);
    if (wasGesture) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      return;
    }
    if (dragRef.current?.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = undefined;
    setDragging(false);
  };

  const onWheel = (event: ReactWheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    zoom(event.deltaY < 0 ? 0.84 : 1.19, event.clientX, event.clientY);
  };

  return (
    <>
      <div
        className="viewer-toolbar"
        role="group"
        aria-label="Fixing layout view controls"
        title="Drag the drawing to pan. Use the mouse wheel or trackpad to zoom. Two fingers zoom and pan on touch screens."
      >
        <div className="viewer-toolbar-controls">
          <button type="button" className="toolbar-button" onClick={() => zoom(0.8)}>Zoom in</button>
          <button type="button" className="toolbar-button" onClick={() => zoom(1.25)}>Zoom out</button>
          <button type="button" className="toolbar-button" onClick={reset}>Fit layout</button>
        </div>
        {onLayerToggle && <SceneLayerMenu scene={scene} layers={layers} onLayerToggle={onLayerToggle} />}
        {variant === 'membrane' && onLayerToggle && (
          <>
            <label className="viewer-toolbar-toggle">
              <input
                type="checkbox"
                checked={showMembraneLabels}
                onChange={(event) => onLayerToggle(MEMBRANE_COURSE_TAG_LAYER, event.target.checked)}
              />
              Show Membrane Labels
            </label>
            <label className="viewer-toolbar-toggle">
              <input
                type="checkbox"
                checked={showFixingTags}
                onChange={(event) => onLayerToggle(FIXING_SPACING_TAG_LAYER, event.target.checked)}
              />
              Show Fixing Spacing
            </label>
          </>
        )}
      </div>
      <svg
        ref={svgRef}
        className="wind-zone-plan-svg"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        role="img"
        aria-label="Backend-authored fixing points, membrane rows and wind-zone geometry; drag to pan, use the mouse wheel or trackpad to zoom, or pinch with two fingers"
        data-scene-id={scene.id}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointerDrag}
        onPointerCancel={endPointerDrag}
        onWheel={onWheel}
        style={{ cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
      >
        {(scene.primitives ?? []).map((primitive) => (
          isPrimitiveVisible(primitive, layers, showMembraneLabels)
            ? renderPrimitive(primitive, drawingSpan, membraneStripHeight, displaySettings)
            : null
        ))}
      </svg>
    </>
  );
}

function SceneLayerMenu({ scene, layers, onLayerToggle }: {
  scene: DrawingScene;
  layers: LayerVisibility;
  onLayerToggle: (layerId: string, visible: boolean) => void;
}) {
  const sceneLayers = (scene.layers ?? []).filter((layer) => layer.id && layer.id !== MEMBRANE_STOCK_METADATA_LAYER);
  if (sceneLayers.length === 0) return null;

  const hidden = sceneLayers.filter((layer) => layers[layer.id ?? ''] === false).length;

  return (
    <details className="viewer-layer-menu">
      <summary className="toolbar-button" aria-label="Drawing layers">
        Layers{hidden > 0 ? ` (${sceneLayers.length - hidden}/${sceneLayers.length})` : ''}
      </summary>
      <div className="viewer-layer-menu-body" role="group" aria-label="Drawing layer visibility">
        {sceneLayers.map((layer) => {
          const id = layer.id ?? '';
          return (
            <label key={id} className="viewer-layer-option">
              <input
                type="checkbox"
                checked={layers[id] !== false}
                onChange={(event) => onLayerToggle(id, event.target.checked)}
              />
              {layer.display_name || id}
            </label>
          );
        })}
      </div>
    </details>
  );
}

function baseSceneViewBox(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  padding: number,
): ViewBoxState {
  return {
    x: bounds.minX - padding,
    y: bounds.minY - padding,
    width: Math.max(bounds.maxX - bounds.minX, 1) + 2 * padding,
    height: Math.max(bounds.maxY - bounds.minY, 1) + 2 * padding,
  };
}

function primitiveLayerVisible(primitive: DrawingPrimitive, layers: LayerVisibility): boolean {
  const layerId = primitive.layer_id ?? '';
  return layerId === '' || layers[layerId] !== false;
}

function isPrimitiveVisible(primitive: DrawingPrimitive, layers: LayerVisibility, showMembraneLabels: boolean): boolean {
  if (primitive.layer_id === MEMBRANE_STOCK_METADATA_LAYER) return false;
  if (!primitiveLayerVisible(primitive, layers)) return false;
  if (primitive.layer_id === MEMBRANE_COURSE_TAG_LAYER && !showMembraneLabels) return false;
  return true;
}

function medianMembraneStripHeight(primitives: readonly DrawingPrimitive[]): number | undefined {
  const heights: number[] = [];
  for (const primitive of primitives) {
    if (primitive?.layer_id !== MEMBRANE_SHEETS_LAYER || primitive.kind !== 'DRAWING_PRIMITIVE_KIND_POLYGON') continue;
    const ys = (primitive.points ?? []).map((point) => point.y ?? 0);
    if (ys.length < 2) continue;
    const height = Math.max(...ys) - Math.min(...ys);
    if (height > 0) heights.push(height);
  }
  if (heights.length === 0) return undefined;
  heights.sort((a, b) => a - b);
  return heights[Math.floor(heights.length / 2)];
}

function membraneLabelFontSize(role: string | undefined, stripHeight: number | undefined, drawingSpan: number): number {
  const drawingFloor = drawingSpan * 0.008;
  const drawingCeiling = drawingSpan * 0.02;
  const stripFloor = stripHeight && stripHeight > 0 ? stripHeight * 0.86 : drawingFloor;
  const stripCeiling = stripHeight && stripHeight > 0 ? stripHeight * 0.95 : drawingCeiling;
  const minSize = Math.min(stripCeiling, Math.max(drawingFloor, stripFloor));
  const maxSize = Math.max(minSize, Math.min(drawingCeiling, stripCeiling));

  if (role?.startsWith(MEMBRANE_LABEL_HEIGHT_PREFIX)) {
    const authored = Number(role.slice(MEMBRANE_LABEL_HEIGHT_PREFIX.length));
    if (Number.isFinite(authored) && authored > 0) {
      return Math.min(maxSize, Math.max(minSize, authored));
    }
  }
  if (!stripHeight || stripHeight <= 0) return Math.min(Math.max(drawingSpan * 0.012, minSize), maxSize);
  return Math.min(Math.max(stripHeight * 0.9, minSize), maxSize);
}

function strokeWidthFor(primitive: DrawingPrimitive): number {
  if (primitive.line_style_token === 'heavy') return 2;
  if (primitive.line_style_token === 'dashed') return 1;
  return 0.75;
}

function renderPrimitive(primitive: DrawingPrimitive, drawingSpan: number, membraneStripHeight: number | undefined, displaySettings: DrawingDisplaySettings) {
  const points = primitive.points ?? [];
  const key = primitive.id ?? `${primitive.kind ?? 'primitive'}-${primitive.draw_order ?? 0}`;
  const stroke = primitive.stroke_token ? tokenColour(primitive.stroke_token, 'var(--text-secondary)') : 'none';
  const fill = primitive.fill_token ? tokenColour(primitive.fill_token, stroke) : 'none';
  const fillOpacity = primitive.fill_token ? tokenFillOpacity(primitive.fill_token) : undefined;
  const strokeWidth = strokeWidthFor(primitive);

  switch (primitive.kind) {
    case 'DRAWING_PRIMITIVE_KIND_POLYGON':
    case 'DRAWING_PRIMITIVE_KIND_HATCH':
      if (points.length < 3) return null;
      return (
        <polygon
          key={key}
          points={points.map((point) => `${point.x ?? 0},${point.y ?? 0}`).join(' ')}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
          data-primitive-kind={primitive.kind}
          data-primitive-id={primitive.id}
          data-layer-id={primitive.layer_id}
        />
      );

    case 'DRAWING_PRIMITIVE_KIND_LINE':
    case 'DRAWING_PRIMITIVE_KIND_BREAK_LINE': {
      const [from, to] = points;
      if (!from || !to) return null;
      return (
        <line
          key={key}
          x1={from.x ?? 0}
          y1={from.y ?? 0}
          x2={to.x ?? 0}
          y2={to.y ?? 0}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={primitive.line_style_token === 'dashed' ? '6 4' : undefined}
          vectorEffect="non-scaling-stroke"
          data-primitive-kind={primitive.kind}
          data-primitive-id={primitive.id}
          data-layer-id={primitive.layer_id}
        />
      );
    }

    case 'DRAWING_PRIMITIVE_KIND_POLYLINE':
      if (points.length < 2) return null;
      return (
        <polyline
          key={key}
          points={points.map((point) => `${point.x ?? 0},${point.y ?? 0}`).join(' ')}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
          data-primitive-kind={primitive.kind}
          data-primitive-id={primitive.id}
          data-layer-id={primitive.layer_id}
        />
      );

    case 'DRAWING_PRIMITIVE_KIND_CIRCLE': {
      const [centre, edge] = points;
      if (!centre || !edge) return null;
      const isFixing = primitive.layer_id === FIXING_POINTS_LAYER;
      const rawRadius = Math.hypot((edge.x ?? 0) - (centre.x ?? 0), (edge.y ?? 0) - (centre.y ?? 0));
      const radius = isFixing ? rawRadius * FIXING_MARKER_SCALE : rawRadius;
      const circleColour = isFixing ? 'var(--ejot-red)' : (fill === 'none' ? stroke : fill);
      const markerStroke = isFixing ? 'var(--surface-workspace)' : circleColour;
      return (
        <circle
          key={key}
          cx={centre.x ?? 0}
          cy={centre.y ?? 0}
          r={radius}
          fill={circleColour}
          stroke={markerStroke}
          strokeWidth={isFixing ? 0.5 : strokeWidth}
          vectorEffect="non-scaling-stroke"
          data-primitive-kind={primitive.kind}
          data-primitive-id={primitive.id}
          data-layer-id={primitive.layer_id}
        />
      );
    }

    case 'DRAWING_PRIMITIVE_KIND_TEXT':
    case 'DRAWING_PRIMITIVE_KIND_SYMBOL':
    case 'DRAWING_PRIMITIVE_KIND_LEADER': {
      const [anchor] = points;
      if (!anchor || !primitive.text) return null;
      const membraneFontSize = membraneLabelFontSize(primitive.text_role_token, membraneStripHeight, drawingSpan);
      const isMembraneCourseTag = primitive.layer_id === MEMBRANE_COURSE_TAG_LAYER;
      const isFixingSpacingTag = primitive.layer_id === FIXING_SPACING_TAG_LAYER;
      const fontSize = isMembraneCourseTag
        ? membraneFontSize * displaySettings.membraneLabelScale
        : isFixingSpacingTag
          ? membraneFontSize * displaySettings.fixingLabelScale
          : drawingSpan * 0.012;
      return (
        <text
          key={key}
          x={anchor.x ?? 0}
          y={anchor.y ?? 0}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={stroke}
          fontFamily="Roboto Condensed, Arial, sans-serif"
          fontSize={fontSize}
          fontWeight={isMembraneCourseTag ? 800 : 600}
          transform={primitive.text_rotation_degrees
            ? `rotate(${primitive.text_rotation_degrees} ${anchor.x ?? 0} ${anchor.y ?? 0})`
            : undefined}
          data-primitive-kind={primitive.kind}
          data-primitive-id={primitive.id}
          data-layer-id={primitive.layer_id}
        >
          {primitive.text}
        </text>
      );
    }

    default:
      return null;
  }
}

function sceneBounds(
  scene: DrawingScene | undefined,
  fallback: { minX: number; minY: number; maxX: number; maxY: number },
) {
  const min = scene?.bounds_min;
  const max = scene?.bounds_max;
  if (min?.x === undefined || min.y === undefined || max?.x === undefined || max.y === undefined) {
    return fallback;
  }
  return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
}

function tokenFillOpacity(token: string): number {
  switch (token) {
    case 'membrane-course-even':
    case 'membrane-course-odd':
      return 1;
    case 'membrane-lap':
      return 0.55;
    default:
      return 0.12;
  }
}

function tokenColour(token: string | undefined, fallback: string): string {
  switch (token) {
    case 'brand':
      return 'var(--ejot-red)';
    case 'zone-corner':
      return 'var(--zone-corner)';
    case 'zone-edge':
      return 'var(--zone-edge)';
    case 'zone-interior':
      return 'var(--zone-interior)';
    case 'zone-inner-border':
      return 'var(--zone-inner-border)';
    case 'outline':
    case 'text':
      return 'var(--text-primary)';
    case 'dimension':
      return 'var(--text-muted)';
    case 'membrane':
      return 'var(--text-secondary)';
    case 'membrane-course-even':
      return 'var(--membrane-course-even)';
    case 'membrane-course-odd':
      return 'var(--membrane-course-odd)';
    case 'membrane-lap':
      return 'var(--membrane-lap)';
    default:
      return fallback;
  }
}
